// src/FeaturesSection.jsx (or your components directory)
import React from "react";
import CVBuildAndOptimize from "../../assets/images/landing/CVBuildAndOptimize.png";
import TailoringCVToJob from "../../assets/images/landing/TailoringCVToJob.png";
import RecommendedJobs from "../../assets/images/landing/RecommendedJobs.png";
import TailoringSkillsToACareerPath from "../../assets/images/landing/TailoringSkillsToACareerPath.png";

// Reusable Image Placeholder Component
const ImagePlaceholder = ({ text, className }) => (
  <div
    className={`bg-white p-6 shadow-xl rounded-xl w-full max-w-lg aspect-video flex items-center justify-center text-center ${
      className || ""
    }`}
    style={{ fontFamily: "Poppins, sans-serif" }}
  >
    <span className="text-gray-500 text-sm md:text-base">{text}</span>
  </div>
);

// Reusable Feature Card Component
const FeatureCard = ({
  title,
  description,
  imagePlaceholderText,
  imageSrc,
  imageAltText = "Feature image",
  imageOnLeft = false,
}) => {
  const textOrder = imageOnLeft ? "md:order-last" : "";
  const imageOrder = imageOnLeft ? "md:order-first" : "";

  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
      <div className="md:flex">
        {/* Text Content Area */}
        <div
          className={`md:w-1/2 p-8 py-10 lg:p-12 flex flex-col justify-center ${textOrder}`}
        >
          <h3
            className="text-2xl lg:text-3xl font-bold mb-4"
            style={{
              color: "#073214",
              fontFamily: "Poppins, sans-serif",
            }}
          >
            {title}
          </h3>
          <p
            className="text-gray-700 text-sm lg:text-base leading-relaxed"
            style={{ fontFamily: "Poppins, sans-serif" }}
          >
            {description}
          </p>
        </div>

        {/* Image Area */}
        <div
          className={`md:w-1/2 bg-slate-50 p-6 lg:p-10 flex items-center justify-center ${imageOrder}`}
        >
          {imageSrc ? (
            <img
              src={imageSrc}
              alt={imageAltText || title}
              className="rounded-xl shadow-xl w-full max-w-lg object-contain"
            />
          ) : (
            <ImagePlaceholder text={imagePlaceholderText} />
          )}
        </div>
      </div>
    </div>
  );
};

const FeaturesSection = () => {
  const featuresData = [
    {
      title: "Tailoring Skills to a Career Path",
      description:
        "Understand the skills landscape for your desired career. SkillMatch AI helps you identify skill gaps and suggests learning paths to achieve your goals.",
      // imagePlaceholderText: "Placeholder: Career Path & Skills UI", // Optional: remove if imageSrc is always provided
      imageSrc: TailoringSkillsToACareerPath, // <--- USE IMPORTED IMAGE
      imageAltText: "Visual representation of skill mapping to career paths", // <--- ADD ALT TEXT
      imageOnLeft: false,
    },
    {
      title: "Resume Building & Optimization",
      description:
        "Build a professional, ATS-friendly Resume from scratch or optimize your existing one. Highlight the right achievements, keywords, and structure to impress recruiters.",
      // imagePlaceholderText: "Placeholder: Profile Match Strength Graph UI", // Optional: remove
      imageSrc: CVBuildAndOptimize, // <--- USE IMPORTED IMAGE (as requested, this is the same as the 4th feature)
      imageAltText: "Interface for Resume building and optimization tools", // <--- ADD ALT TEXT
      imageOnLeft: true,
    },
    {
      title: "Recommended Jobs",
      description:
        "Receive AI-powered job recommendations tailored to your unique skillset, experience, and growth potential. Real-time job matching, updated regularly.",
      // imagePlaceholderText: "Placeholder: Recommended Jobs List / Select Role UI", // Optional: remove
      imageSrc: RecommendedJobs, // <--- USE IMPORTED IMAGE
      imageAltText:
        "List of AI-powered recommended jobs tailored to user profile", // <--- ADD ALT TEXT
      imageOnLeft: false,
    },
    {
      title: "Tailoring Resumes to Job",
      description:
        "No generic templates here. SkillMatch AI dynamically customizes your Resume to match the exact requirements of each job application, maximizing interview chances.",
      imageSrc: TailoringCVToJob,
      imageAltText: "Interface showing Resume tailoring for a specific job",
      imageOnLeft: true,
    },
  ];

  return (
    <section
      className="py-16 lg:py-24 bg-[#F9F9F9]"
      style={{ fontFamily: "Poppins, sans-serif" }}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mb-12 lg:mb-20">
          <div className="lg:flex lg:justify-between lg:items-start">
            <div className="lg:w-2/3 text-center lg:text-left">
              <p
                className="text-orange-500 text-sm font-semibold uppercase tracking-wider"
                style={{ fontFamily: "Poppins, sans-serif" }}
              >
                Our Features
              </p>
              <h2
                className="text-3xl md:text-4xl lg:text-5xl font-bold mt-2"
                style={{
                  color: "#073214",
                  fontFamily: "Geist, sans-serif",
                }}
              >
                Discover Our <br /> Features
              </h2>
            </div>
            <div className="lg:w-1/3 mt-4 lg:mt-2 lg:pl-8 text-center lg:text-left">
              <p
                className="text-gray-600 text-sm md:text-base leading-relaxed"
                style={{ fontFamily: "Poppins, sans-serif" }}
              >
                From smart Resume optimization to personalized job and career
                path suggestions, SkillMatch AI is your key to standing out and
                advancing—effortlessly.
              </p>
            </div>
          </div>
        </div>

        {/* Feature Cards */}
        <div className="space-y-12 lg:space-y-16">
          {featuresData.map((feature, index) => (
            <FeatureCard
              key={index}
              title={feature.title}
              description={feature.description}
              imagePlaceholderText={feature.imagePlaceholderText}
              imageSrc={feature.imageSrc}
              imageAltText={feature.imageAltText}
              imageOnLeft={feature.imageOnLeft}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
